/**
 * PatternAssignmentCard (TP-Patterns.v1 Phase 2 + Chunk 3 polish).
 *
 * A read/write card that lives on the employee edit dialog.
 * Answers the three questions every manager asks:
 *   1. What pattern controls this employee today?
 *   2. When did it start?
 *   3. What does "normal" look like for this person?
 *
 * Chunk 3 (Feb 2026) polish:
 *   * Type badge shows the customer-facing setup template label
 *     (Night cover / Service / split-day cover / etc) when
 *     `setup_template_key` is present. Falls back to the backend
 *     scheduling subtype for legacy patterns.
 *   * Summary block shows plain-English "usual days" + "expected
 *     weekly hours" (or min/max hours for flex, or "No guaranteed
 *     hours" for zero-hours) + optional notes + week labels for
 *     rotating cycles.
 *   * No warning / dashboard / reporting interpretation. Working
 *     Patterns remain display-only.
 *
 * Behaviour:
 *   * NO consumer switch -- the card is informational + assignment
 *     only. Roster / RSA / Coverage Guard still read legacy fields
 *     from `employees.working_pattern` / `employees.shift_pattern_id`.
 *   * Change pattern opens a picker + effective-from date. Backend
 *     closes the previous open assignment atomically.
 *   * History link shows the audit trail.
 */
import { useEffect, useState } from "react";
import { api, formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
  DialogFooter, DialogClose,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { RefreshCcw, Clock, History } from "lucide-react";

function hoursLabel(mins) {
  if (!mins) return "0h";
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m === 0 ? `${h}h` : `${h}h ${m}m`;
}

function todayIso() { return new Date().toISOString().slice(0, 10); }

// Backend scheduling subtype -> plain-English label. Used as the
// fallback badge when a pattern has no `setup_template_key`
// (i.e. legacy rows created before Chunk 2).
const BACKEND_TYPE_LABEL = {
  scheduled_cycle: "Fixed / rotating",
  flexible_weekly: "Flexible weekly",
  zero_hours: "Zero-hours",
};

// Customer-facing setup template labels (Chunk 2). Wins over the
// backend subtype in the badge whenever the pattern has a
// `setup_template_key`. Kept in sync with the Working Patterns
// picker so a manager sees the same phrase they picked.
const SETUP_TEMPLATE_LABEL = {
  standard_weekly: "Standard weekly",
  rotating_schedule: "Rotating schedule",
  night_cover: "Night cover",
  service_split_day: "Service / split-day cover",
  flexible_weekly: "Flexible weekly",
  zero_hours: "Zero-hours / casual",
  weekend_part_time: "Weekend / part-time",
  custom: "Custom",
};

const WEEKDAY_SHORT = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

// Render a sorted list of weekday integers (0=Mon .. 6=Sun) as a
// friendly plain-English phrase. Contiguous common cases become
// "Mon-Fri" / "Sat-Sun" / "Every day"; anything else is a comma
// list. Empty list returns null.
function formatDayList(days) {
  if (!days || days.length === 0) return null;
  const s = [...days].sort((a, b) => a - b);
  if (s.length === 7) return "Every day";
  if (s.join(",") === "0,1,2,3,4") return "Mon-Fri";
  if (s.join(",") === "5,6") return "Sat-Sun";
  // Detect a single contiguous range and render as "Mon-Wed" etc.
  let contiguous = true;
  for (let i = 1; i < s.length; i++) {
    if (s[i] !== s[i - 1] + 1) { contiguous = false; break; }
  }
  if (contiguous && s.length >= 3) {
    return `${WEEKDAY_SHORT[s[0]]}-${WEEKDAY_SHORT[s[s.length - 1]]}`;
  }
  return s.map((d) => WEEKDAY_SHORT[d]).join(", ");
}

function fmtHours(mins) {
  if (!mins) return "0h";
  const h = mins / 60;
  return Number.isInteger(h) ? `${h}h` : `${(Math.round(h * 10) / 10)}h`;
}

// Derive plain-English summary lines from a Pattern doc. Returns an
// array of strings (0-4 lines). Never throws; if the pattern shape
// is unfamiliar it degrades to a single generic line.
function summaryLines(pattern) {
  if (!pattern) return [];
  const out = [];
  if (pattern.type === "scheduled_cycle") {
    const sc = pattern.scheduled_cycle || {};
    const cycle = sc.cycle_length_weeks || 1;
    const weeks = sc.weeks || [];
    // Union of days that carry blocks in any week.
    const dayHits = new Set();
    let totalMinutes = 0;
    weeks.forEach((w) => {
      (w.days || []).forEach((d) => {
        if (d.is_off || !(d.blocks || []).length) return;
        dayHits.add(d.weekday);
        (d.blocks || []).forEach((b) => { totalMinutes += b.duration_minutes || 0; });
      });
    });
    const usualDays = Array.from(dayHits).sort((a, b) => a - b);
    const perWeekMin = weeks.length ? Math.round(totalMinutes / weeks.length) : 0;
    if (cycle > 1) {
      out.push(`${cycle}-week rotation.`);
    }
    if (perWeekMin > 0) {
      out.push(`Expected ${fmtHours(perWeekMin)}/week.`);
    }
    const days = formatDayList(usualDays);
    if (days) out.push(`Usual days ${days}.`);
    return out;
  }
  if (pattern.type === "flexible_weekly") {
    const fw = pattern.flexible_weekly || {};
    const mn = fw.min_weekly_minutes || 0;
    const mx = fw.max_weekly_minutes || 0;
    if (mn || mx) {
      if (mn === mx) {
        out.push(`Expected ${fmtHours(mn)}/week.`);
      } else {
        // 20-30h/week (single unit suffix reads cleaner than 20h-30h/week).
        const mnH = mn / 60;
        const mxH = mx / 60;
        const mnLabel = Number.isInteger(mnH) ? `${mnH}` : `${Math.round(mnH * 10) / 10}`;
        out.push(`${mnLabel}-${fmtHours(mx)}/week.`);
      }
    }
    const days = formatDayList(fw.working_days);
    if (days) out.push(`Available days ${days}.`);
    return out;
  }
  if (pattern.type === "zero_hours") {
    const zh = pattern.zero_hours || {};
    out.push("No guaranteed hours.");
    const days = formatDayList(zh.working_days);
    if (days) out.push(`Available days ${days}.`);
    if (zh.legal_max_weekly_minutes) {
      out.push(`Weekly cap ${fmtHours(zh.legal_max_weekly_minutes)}.`);
    }
    return out;
  }
  return [];
}

export default function PatternAssignmentCard({ employeeId, canAssign = true, canRead = true }) {
  const [state, setState] = useState({ loading: true, assignment: null, pattern: null });
  const [changer, setChanger] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);

  const load = async () => {
    if (!employeeId || !canRead) { setState({ loading: false, assignment: null, pattern: null }); return; }
    setState((s) => ({ ...s, loading: true }));
    try {
      const { data } = await api.get(`/employees/${employeeId}/pattern-assignment/active`);
      setState({ loading: false, assignment: data.assignment, pattern: data.pattern });
    } catch (err) {
      // 404 (employee not found) or 403 (out of scope) -- degrade to read-only.
      setState({ loading: false, assignment: null, pattern: null });
    }
  };
  useEffect(() => { load(); }, [employeeId]);

  if (!employeeId) return null;

  const pattern = state.pattern;
  const badgeLabel = pattern
    ? (SETUP_TEMPLATE_LABEL[pattern.setup_template_key]
        || BACKEND_TYPE_LABEL[pattern.type]
        || pattern.type)
    : null;
  const lines = summaryLines(pattern);

  return (
    <div className="rounded-md border p-3 space-y-2"
          data-testid="pattern-assignment-card">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm font-medium">
          <Clock className="h-4 w-4" /> Working pattern
        </div>
        <div className="flex gap-1">
          <Button size="sm" variant="ghost" onClick={() => setHistoryOpen(true)}
                  data-testid="pac-history"
                  title="View pattern history">
            <History className="h-4 w-4" />
          </Button>
          {canAssign && (
            <Button size="sm" variant="ghost" onClick={() => setChanger(true)}
                    data-testid="pac-change"
                    title="Change pattern">
              <RefreshCcw className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>

      {state.loading ? (
        <div className="text-xs text-muted-foreground">Loading...</div>
      ) : !pattern ? (
        <div className="text-xs text-muted-foreground" data-testid="pac-empty">
          No working pattern assigned yet. {canAssign
            ? <>Click <em>Change pattern</em> to assign one.</>
            : "Ask a manager to assign one."}
        </div>
      ) : (
        <div className="text-sm space-y-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium" data-testid="pac-active-name">{pattern.name}</span>
            <Badge variant="secondary" data-testid="pac-active-type">
              {badgeLabel}
            </Badge>
          </div>
          {lines.length > 0 && (
            <div className="text-xs text-muted-foreground" data-testid="pac-summary">
              {lines.join(" ")}
            </div>
          )}
          {pattern.description && (
            <div className="text-xs text-muted-foreground" data-testid="pac-notes">
              Notes: {pattern.description}
            </div>
          )}
          <div className="text-xs text-muted-foreground">
            Effective from: <span data-testid="pac-effective-from">{state.assignment?.effective_from || "-"}</span>
          </div>
        </div>
      )}

      {changer && (
        <ChangePatternDialog
          employeeId={employeeId}
          current={state.assignment}
          onClose={() => setChanger(false)}
          onSaved={() => { setChanger(false); load(); }}
        />
      )}

      {historyOpen && (
        <HistoryDialog
          employeeId={employeeId}
          onClose={() => setHistoryOpen(false)}
        />
      )}
    </div>
  );
}

// -------------------------------------------------------------------
// Change pattern
// -------------------------------------------------------------------
function ChangePatternDialog({ employeeId, current, onClose, onSaved }) {
  const [patterns, setPatterns] = useState([]);
  const [chosen, setChosen] = useState("");
  const [effectiveFrom, setEffectiveFrom] = useState(todayIso());
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/patterns?include_archived=false");
        setPatterns(data || []);
      } catch (err) {
        toast.error(formatError(err.response?.data?.detail) || "Could not load patterns");
      }
    })();
  }, []);

  const submit = async () => {
    if (!chosen) { toast.error("Pick a pattern first"); return; }
    setSaving(true);
    try {
      await api.post(`/employees/${employeeId}/pattern-assignments`, {
        employee_id: employeeId,
        pattern_id: chosen,
        effective_from: effectiveFrom,
        notes: notes || null,
      });
      toast.success("Pattern assigned");
      onSaved();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "Assignment failed");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="pac-change-dialog">
        <DialogHeader>
          <DialogTitle>Change working pattern</DialogTitle>
          <DialogDescription>
            The new pattern takes effect on the selected date. The previous pattern is closed on the day before, so this employee always has exactly one active pattern.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Pick a pattern</Label>
            <Select value={chosen} onValueChange={setChosen}>
              <SelectTrigger data-testid="pac-picker"><SelectValue placeholder="Choose..." /></SelectTrigger>
              <SelectContent>
                {patterns.map((p) => (
                  <SelectItem key={p.pattern_id} value={p.pattern_id}
                                data-testid={`pac-option-${p.pattern_id}`}>
                    {p.name} - {SETUP_TEMPLATE_LABEL[p.setup_template_key] || BACKEND_TYPE_LABEL[p.type] || p.type}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Effective from</Label>
            <Input type="date" value={effectiveFrom}
                    onChange={(e) => setEffectiveFrom(e.target.value)}
                    data-testid="pac-effective-input" />
          </div>
          <div>
            <Label>Notes (optional)</Label>
            <Input value={notes} onChange={(e) => setNotes(e.target.value)}
                    placeholder="e.g. Promotion to full-time" />
          </div>
          {current && (
            <div className="text-xs text-muted-foreground border-l-2 pl-2">
              Current pattern started {current.effective_from}. It will be closed on the day before your chosen date so no overlap can happen.
            </div>
          )}
        </div>
        <DialogFooter>
          <DialogClose asChild><Button variant="outline" data-testid="pac-change-cancel">Cancel</Button></DialogClose>
          <Button onClick={submit} disabled={!chosen || saving} data-testid="pac-change-save">
            {saving ? "Saving..." : "Assign pattern"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// -------------------------------------------------------------------
// History
// -------------------------------------------------------------------
function HistoryDialog({ employeeId, onClose }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/employees/${employeeId}/pattern-assignments`);
        setRows(data || []);
      } catch (err) {
        toast.error(formatError(err.response?.data?.detail) || "Could not load history");
      } finally { setLoading(false); }
    })();
  }, [employeeId]);

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-xl" data-testid="pac-history-dialog">
        <DialogHeader>
          <DialogTitle>Pattern assignment history</DialogTitle>
          <DialogDescription>
            Most recent first. The row without an &quot;end&quot; date is the currently active pattern.
          </DialogDescription>
        </DialogHeader>
        {loading ? (
          <div className="text-sm text-muted-foreground">Loading...</div>
        ) : rows.length === 0 ? (
          <div className="text-sm text-muted-foreground">No pattern assignments yet.</div>
        ) : (
          <div className="space-y-2">
            {rows.map((r) => (
              <div key={r.assignment_id}
                    data-testid={`pac-history-row-${r.assignment_id}`}
                    className="rounded border p-2 text-sm">
                <div className="flex items-center justify-between">
                  <div>
                    <span className="font-medium">{r.effective_from}</span>
                    <span className="text-muted-foreground"> {"->"} </span>
                    <span>{r.effective_to || <em className="text-primary">active</em>}</span>
                  </div>
                  <Badge variant="outline" className="text-xs">
                    ~{hoursLabel(r.snapshot_expected_daily_minutes)}/day
                  </Badge>
                </div>
                {r.notes && (
                  <div className="text-xs text-muted-foreground mt-1">{r.notes}</div>
                )}
              </div>
            ))}
          </div>
        )}
        <DialogFooter>
          <DialogClose asChild>
            <Button data-testid="pac-history-close">Close</Button>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
